import { useEffect } from "react";

import { QuestionnaireItem } from "@medplum/fhirtypes";

import {
  Input,
  NumberInput,
  Radio,
  rem,
  SimpleGrid,
  Stack,
  Switch,
  Title,
  TitleOrder,
} from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { UseFormReturnType } from "@mantine/form";

import { IconCalendar } from "@tabler/icons-react";
import { formatISO, parseISO, startOfDay } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- TODO: migrate to date-fns (grandfathered legacy dayjs usage)
import dayjs from "dayjs";

import { EddAnswersFormType } from "@/clients/zod-schemas/edd-form";
import QuestionnaireNativeSelect from "@/intake-forms/components/QuestionnaireNativeSelect";
import {
  calculateEDDFromLastMenstrualPeriod,
  calculateEDDFromUltrasoundDate,
} from "@/pregnancy/utils/edd";
import { type MantineDateInputValue, parseMantineDateInputValue } from "@/utils/mantine-date-input";

type EddFormProps = {
  form: UseFormReturnType<EddAnswersFormType>;
  eddSelectGroup: QuestionnaireItem;
  groupTitleOrder: TitleOrder | undefined;
  isCurrentlyPregnant: boolean;
};

const MAX_GA_WEEKS = 152;
const MAX_GA_DAYS = 6;

/** Converts a Mantine DateInput value to a YYYY-MM-DD string for EDD calculations. */
function toIsoDateString(value: Date | string): string {
  if (typeof value === "string") return value;
  return formatISO(startOfDay(value), { representation: "date" });
}

const calculateUltrasoundEDD = (
  ultrasoundDate: Date | string,
  weeks: string | number | undefined,
  days: string | number | undefined,
) => {
  return parseISO(calculateEDDFromUltrasoundDate(toIsoDateString(ultrasoundDate), weeks, days));
};

const calculateLmpEDD = (lmpDate: MantineDateInputValue): Date | undefined => {
  if (lmpDate === null) return undefined;

  const isoDate = toIsoDateString(parseMantineDateInputValue(lmpDate));
  return parseISO(calculateEDDFromLastMenstrualPeriod(isoDate));
};

export default function EddForm({
  form,
  eddSelectGroup,
  groupTitleOrder,
  isCurrentlyPregnant,
}: EddFormProps) {
  useEffect(() => {
    if (!isCurrentlyPregnant) {
      form.setFieldValue("customEddDate", undefined);
      form.setFieldValue("ultrasoundEddDate", undefined);
      form.setFieldValue("lmpEddDate", undefined);
      form.setFieldValue("ultrasoundDate", undefined);
      form.setFieldValue("ultrasoundGestationalAgeWeeks", undefined);
      form.setFieldValue("ultrasoundGestationalAgeDays", undefined);
      form.setFieldValue("lmpDate", undefined);
      form.setFieldValue("lmpCertain", undefined);
      form.setFieldValue("eddDisplay", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCurrentlyPregnant]);

  const ultrasoundDateValue = form.values.ultrasoundDate;
  const ultrasoundGaInputsKey =
    ultrasoundDateValue instanceof Date
      ? ultrasoundDateValue.getTime()
      : ultrasoundDateValue
        ? String(ultrasoundDateValue)
        : "no-ultrasound-date";

  return (
    <Stack gap="xl" mt={rem(8)}>
      {!isCurrentlyPregnant && (
        <Switch
          color="burgundy.9"
          label="Is the client pregnant?"
          checked={form.values.isPregnant || false}
          data-testid="edd-drawer-is-pregnant-switch"
          onChange={(event) => form.setFieldValue("isPregnant", event.currentTarget.checked)}
        />
      )}

      <QuestionnaireNativeSelect
        form={form}
        required={true}
        name="eddDisplay"
        group={eddSelectGroup}
        placeholder="Select EDD type"
        emptyOptionLabel="Select EDD type"
        disabled={!form.values.isPregnant}
        data-testid="edd-drawer-edd-display-select"
        label="Which EDD do you want to display on side panel?"
      />

      {/* LMP EDD Section */}
      <Stack mt={rem(8)}>
        <Title c="gray.6" order={groupTitleOrder}>
          LMP EDD
        </Title>
        <DateInput
          clearable
          label="LMP"
          allowDeselect={false}
          placeholder="MM/DD/YYYY"
          valueFormat="MM/DD/YYYY"
          maxDate={dayjs().toDate()}
          data-testid="edd-drawer-lmp-date-input"
          leftSection={<IconCalendar size={16} />}
          {...form.getInputProps("lmpDate")}
          disabled={!form.values.isPregnant}
          onChange={(date) => {
            const originalOnChange = form.getInputProps("lmpDate").onChange;

            originalOnChange?.(date);
            if (date) {
              form.setFieldValue("lmpEddDate", calculateLmpEDD(date));
            } else {
              form.setFieldValue("lmpEddDate", undefined);
            }
          }}
        />
        <Radio.Group label="LMP Certain" {...form.getInputProps("lmpCertain")}>
          <Radio
            mt="md"
            label="Yes"
            color="burgundy.9"
            value="LMP Certain"
            disabled={!form.values.isPregnant}
            data-testid="edd-drawer-lmp-certain-yes"
          />
          <Radio
            mt="md"
            label="No"
            color="burgundy.9"
            value="LMP Uncertain"
            disabled={!form.values.isPregnant}
            data-testid="edd-drawer-lmp-certain-no"
          />
        </Radio.Group>
        <DateInput
          label="EDD"
          readOnly={true}
          placeholder="MM/DD/YYYY"
          valueFormat="MM/DD/YYYY"
          data-testid="edd-drawer-lmp-edd-date-input"
          {...form.getInputProps("lmpEddDate")}
          disabled={!form.values.isPregnant}
        />
      </Stack>

      {/* Ultrasound EDD Section */}
      <Stack mt={rem(8)}>
        <Title c="gray.6" order={groupTitleOrder}>
          Ultrasound EDD
        </Title>
        <DateInput
          clearable
          allowDeselect={false}
          label="Ultrasound Date"
          placeholder="MM/DD/YYYY"
          valueFormat="MM/DD/YYYY"
          maxDate={dayjs().toDate()}
          leftSection={<IconCalendar size={16} />}
          data-testid="edd-drawer-ultrasound-date-input"
          {...form.getInputProps("ultrasoundDate")}
          disabled={!form.values.isPregnant}
          onChange={(date) => {
            const originalOnChange = form.getInputProps("ultrasoundDate").onChange;
            originalOnChange?.(date);

            if (date) {
              const weeks = form.values.ultrasoundGestationalAgeWeeks;
              const days = form.values.ultrasoundGestationalAgeDays;
              const ultrasoundEdd = calculateUltrasoundEDD(date, weeks, days);
              form.setFieldValue("ultrasoundEddDate", ultrasoundEdd);
            } else {
              form.setFieldValue("ultrasoundEddDate", undefined);
              form.setFieldValue("ultrasoundGestationalAgeWeeks", undefined);
              form.setFieldValue("ultrasoundGestationalAgeDays", undefined);
            }
          }}
        />
        {/* eslint-disable-next-line quilted/require-responsive-grid -- labels fill the first row and inputs the second, so the two columns stay paired; each is about 170px in a phone's full-width drawer */}
        <SimpleGrid cols={2} verticalSpacing={0}>
          <Input.Label htmlFor="ultrasoundGestationalAgeWeeks">
            Ultrasound Gestational Age Weeks
          </Input.Label>
          <Input.Label htmlFor="ultrasoundGestationalAgeDays">
            Ultrasound Gestational Age Days
          </Input.Label>
          <NumberInput
            min={0}
            max={MAX_GA_WEEKS}
            placeholder="Weeks pregnant"
            id="ultrasoundGestationalAgeWeeks"
            data-testid="edd-drawer-ultrasound-ga-weeks"
            {...form.getInputProps("ultrasoundGestationalAgeWeeks")}
            disabled={!form.values.isPregnant}
            key={`ultrasound-ga-weeks-${ultrasoundGaInputsKey}`}
            onChange={(weeksInput) => {
              const originalOnChange = form.getInputProps("ultrasoundGestationalAgeWeeks").onChange;
              originalOnChange?.(weeksInput);

              const date = form.values.ultrasoundDate;
              const days = form.values.ultrasoundGestationalAgeDays;
              const weeks = Number(weeksInput);

              if (date && weeks <= MAX_GA_WEEKS) {
                const ultrasoundEdd = calculateUltrasoundEDD(date, weeks, days);
                form.setFieldValue("ultrasoundEddDate", ultrasoundEdd);
              }
            }}
          />
          <NumberInput
            min={0}
            max={MAX_GA_DAYS}
            placeholder="Days pregnant"
            id="ultrasoundGestationalAgeDays"
            data-testid="edd-drawer-ultrasound-ga-days"
            {...form.getInputProps("ultrasoundGestationalAgeDays")}
            disabled={!form.values.isPregnant}
            key={`ultrasound-ga-days-${ultrasoundGaInputsKey}`}
            onChange={(daysInput) => {
              const originalOnChange = form.getInputProps("ultrasoundGestationalAgeDays").onChange;
              originalOnChange?.(daysInput);

              const date = form.values.ultrasoundDate;
              const weeks = form.values.ultrasoundGestationalAgeWeeks;
              const days = Number(daysInput || 0);
              if (date && days <= MAX_GA_DAYS) {
                const ultrasoundEdd = calculateUltrasoundEDD(date, weeks, days);
                form.setFieldValue("ultrasoundEddDate", ultrasoundEdd);
              }
            }}
          />
        </SimpleGrid>
        <DateInput
          readOnly={true}
          label="Ultrasound EDD"
          placeholder="MM/DD/YYYY"
          valueFormat="MM/DD/YYYY"
          data-testid="edd-drawer-ultrasound-edd-date-input"
          {...form.getInputProps("ultrasoundEddDate")}
          disabled={!form.values.isPregnant}
        />
      </Stack>

      {/* Custom EDD Section */}
      <Stack mt={rem(8)}>
        <Title c="gray.6" order={groupTitleOrder}>
          Custom EDD
        </Title>
        <DateInput
          clearable
          label="Custom EDD"
          allowDeselect={false}
          placeholder="MM/DD/YYYY"
          valueFormat="MM/DD/YYYY"
          leftSection={<IconCalendar size={16} />}
          {...form.getInputProps("customEddDate")}
          disabled={!form.values.isPregnant}
        />
      </Stack>
    </Stack>
  );
}
